// Always use production backend on Render
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dashboard-backend-24yg.onrender.com';

export interface LoginResponse {
  token: string;
  employee: {
    id: string;
    name: string;
    username: string;
    initials?: string;
    color?: string;
    profilePic?: string;
    bio?: string;
    title?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
  };
}

export interface LoginError extends Error {
  error: string;
  remainingAttempts: number;
  lockedOut?: boolean;
}

const createLoginError = (
  message: string,
  remainingAttempts: number = 5,
  lockedOut: boolean = false
): LoginError => {
  const error = new Error(message) as LoginError;
  error.error = message;
  error.remainingAttempts = remainingAttempts;
  if (lockedOut !== undefined) {
    error.lockedOut = lockedOut;
  }
  return error;
};

export const login = async (username: string, password: string): Promise<LoginResponse> => {
  // Create AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  console.log('Attempting login to:', API_BASE_URL);

  try {
    const response = await fetch(`${API_BASE_URL}/api/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    console.log('Login response status:', response.status);

    // Try to parse JSON, but handle non-JSON responses
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      const text = await response.text();
      console.error('Failed to parse response as JSON:', text);
      throw createLoginError(
        `Server error: ${response.status} ${response.statusText}`
      );
    }

    if (!response.ok) {
      console.error('Login failed:', data);
      throw createLoginError(
        data.error || 'Login mislukt',
        data.remainingAttempts !== undefined ? data.remainingAttempts : 5,
        data.lockedOut || false
      );
    }

    console.log('Login successful');
    return data as LoginResponse;
  } catch (error: any) {
    clearTimeout(timeoutId);
    
    console.error('Login error:', error);
    
    if (error.name === 'AbortError') {
      throw createLoginError(
        'Verbinding timeout. Controleer of de backend server draait.'
      );
    }
    
    // Re-throw if it's already a LoginError
    if (error.remainingAttempts !== undefined) {
      throw error;
    }
    
    // Network or other error
    const errorMessage = error.message || 'Onbekende fout';
    throw createLoginError(
      `Kan niet verbinden met de server (${API_BASE_URL}). Fout: ${errorMessage}`
    );
  }
};

export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/verify`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    const data = await response.json();
    return data.valid === true;
  } catch (error) {
    return false;
  }
};

